/**
 * FeedbackDrawer — feedback detail + reply composer + close/reopen
 * (STORY-010-005). Uses the shared focus-trapped `BottomSheet`.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill, Skeleton } from "@garden/ui";
import { ApiError } from "@/lib/api-client";
import { ADMIN_NS } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import {
  useCloseFeedback,
  useFeedbackDetail,
  useReopenFeedback,
  useReplyFeedback,
} from "@/features/admin/feedback/hooks";
import { ReplyComposer } from "@/features/admin/feedback/ReplyComposer";

function statusTone(status: string): "warn" | "success" | "neutral" {
  switch (status) {
    case "open":
      return "warn";
    case "replied":
      return "success";
    default:
      return "neutral";
  }
}

function FieldRow({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="text-sm text-[var(--g-secondary)]">{label}</dt>
      <dd className="text-right text-sm font-medium">{value}</dd>
    </div>
  );
}

export function FeedbackDrawer({
  feedbackId,
  onClose,
}: {
  feedbackId: string | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const { data: detail, isLoading } = useFeedbackDetail(feedbackId);
  const reply = useReplyFeedback();
  const close = useCloseFeedback();
  const reopen = useReopenFeedback();

  const isOpen = feedbackId !== null;
  const busy = reply.isPending || close.isPending || reopen.isPending;

  const errCode = (e: Error): string =>
    e instanceof ApiError && e.code ? e.code : "unknown";

  const onSend = (subject: string, bodyText: string): void => {
    if (feedbackId === null) return;
    reply.mutate(
      { id: feedbackId, body: { subject, body_text: bodyText } },
      {
        onSuccess: () => toast.success(t("feedback.reply.sent")),
        onError: (e) =>
          toast.error(t("feedback.reply.error", { code: errCode(e) })),
      },
    );
  };

  const onCloseAction = (): void => {
    if (feedbackId === null) return;
    close.mutate(feedbackId, {
      onSuccess: () => toast.success(t("feedback.action.closed")),
      onError: () => toast.error(t("feedback.action.error")),
    });
  };

  const onReopenAction = (): void => {
    if (feedbackId === null) return;
    reopen.mutate(feedbackId, {
      onSuccess: () => toast.success(t("feedback.action.reopened")),
      onError: () => toast.error(t("feedback.action.error")),
    });
  };

  return (
    <BottomSheet open={isOpen} onClose={onClose} title={t("feedback.detail.title")} desktop={isDesktop}>
      {isLoading || !detail ? (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="feedback-drawer-body">
          <div className="flex items-center gap-2">
            <GPill tone={statusTone(detail.status)} size="sm">
              {t(`feedback.status.${detail.status}`, { defaultValue: detail.status })}
            </GPill>
            {detail.spam_score >= 80 ? (
              <GPill tone="error" size="sm">
                {t("feedback.spamBadge")}
              </GPill>
            ) : null}
          </div>

          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
              {t("feedback.detail.message")}
            </h3>
            <p
              data-testid="feedback-message"
              className="whitespace-pre-wrap rounded-[var(--g-radius-sm)] bg-[var(--g-elevated)] p-2 text-sm"
            >
              {detail.message}
            </p>
          </section>

          <dl className="divide-y divide-[var(--g-hairline)]">
            <FieldRow
              label={t("feedback.detail.rating")}
              value={detail.rating !== null ? String(detail.rating) : "—"}
            />
            <FieldRow
              label={t("feedback.detail.contactEmail")}
              value={detail.contact_email ?? t("feedback.detail.noEmail")}
            />
            <FieldRow label={t("feedback.detail.spamScore")} value={String(detail.spam_score)} />
            {detail.admin_reply_text ? (
              <FieldRow label={t("feedback.detail.lastReply")} value={detail.admin_reply_text} />
            ) : null}
          </dl>

          {/* Reply composer — only when not closed. */}
          {detail.status !== "closed" ? (
            <ReplyComposer
              canSend={detail.contact_email !== null}
              pending={reply.isPending}
              defaultSubject={t("feedback.reply.defaultSubject")}
              onSend={onSend}
            />
          ) : null}

          {/* Close / Reopen */}
          <div className="flex justify-end gap-2" data-testid="feedback-lifecycle-actions">
            {detail.status === "closed" ? (
              <Button
                variant="secondary"
                size="sm"
                data-testid="feedback-reopen"
                disabled={busy}
                onClick={onReopenAction}
              >
                {t("feedback.action.reopen")}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                data-testid="feedback-close"
                disabled={busy}
                onClick={onCloseAction}
              >
                {t("feedback.action.close")}
              </Button>
            )}
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

export default FeedbackDrawer;
